import { defineStore } from 'pinia'
import { apiCombo } from '../api'
import {
  isComboAvailable,
  validateComboSelections,
  calculateComboPrice,
  getDefaultComboSelections,
} from '../lib/comboUtils'
import type { Combo, ComboSelection } from './types'

interface ComboState {
  combos: Combo[]
  loading: boolean
  error: string | null
  currentCombo: Combo | null
  currentSelections: ComboSelection[]
}

export const useComboStore = defineStore('combo', {
  state: (): ComboState => ({
    combos: [],
    loading: false,
    error: null,
    currentCombo: null,
    currentSelections: [],
  }),

  getters: {
    activeCombos: (state): Combo[] => {
      return state.combos.filter((combo) => isComboAvailable(combo))
    },
    getComboById: (state) => {
      return (comboId: string | number): Combo | undefined => {
        return state.combos.find((c) => c.id.toString() === comboId.toString())
      }
    },
    getCombosByCategory: (state) => {
      return (categoryId: string | number): Combo[] => {
        return state.combos.filter(
          (c) =>
            c.category_id.toString() === categoryId.toString() &&
            isComboAvailable(c),
        )
      }
    },
    isCurrentSelectionsValid: (state): boolean => {
      if (!state.currentCombo) return false
      return validateComboSelections(state.currentCombo, state.currentSelections).valid
    },
    currentSelectionsErrors: (state): string[] => {
      if (!state.currentCombo) return []
      return validateComboSelections(state.currentCombo, state.currentSelections).errors
    },
    currentComboPrice: (state) => {
      if (!state.currentCombo) return null
      return calculateComboPrice(state.currentCombo, state.currentSelections)
    },
  },

  actions: {
    async fetchCombosForBranch(branchId: string | number) {
      this.loading = true
      this.error = null
      try {
        const { data } = await apiCombo.getCombosForBranch(branchId)
        this.combos = data?.combos ?? []
      } catch (error: unknown) {
        this.error = error instanceof Error ? error.message : 'Ошибка при загрузке комбо'
        throw error
      } finally {
        this.loading = false
      }
    },
    setCurrentCombo(combo: Combo | null) {
      this.currentCombo = combo
      this.currentSelections = combo ? getDefaultComboSelections(combo) : []
    },
    updateGroupSelection(groupId: string | number, productIds: (string | number)[]) {
      const idx = this.currentSelections.findIndex(
        (s) => s.group_id.toString() === groupId.toString(),
      )
      if (idx >= 0) {
        if (productIds.length === 0) this.currentSelections.splice(idx, 1)
        else this.currentSelections[idx].product_ids = productIds
      } else if (productIds.length > 0) {
        this.currentSelections.push({ group_id: groupId, product_ids: productIds })
      }
    },
    toggleProductInGroup(
      groupId: string | number,
      productId: string | number,
      maxSelections: number,
    ) {
      const selection = this.currentSelections.find(
        (s) => s.group_id.toString() === groupId.toString(),
      )
      if (!selection) {
        this.currentSelections.push({ group_id: groupId, product_ids: [productId] })
        return
      }
      const pidx = selection.product_ids.findIndex((id) => id.toString() === productId.toString())
      if (pidx >= 0) selection.product_ids.splice(pidx, 1)
      else if (selection.product_ids.length < maxSelections) selection.product_ids.push(productId)
    },
    isProductSelected(groupId: string | number, productId: string | number): boolean {
      const selection = this.currentSelections.find(
        (s) => s.group_id.toString() === groupId.toString(),
      )
      return selection?.product_ids.some((id) => id.toString() === productId.toString()) ?? false
    },
    async addCurrentComboToCart(branchId: string | number, quantity = 1): Promise<void> {
      if (!this.currentCombo) throw new Error('Комбо не выбрано')
      const validation = validateComboSelections(this.currentCombo, this.currentSelections)
      if (!validation.valid) throw new Error(validation.errors.join(', '))
      await apiCombo.addComboToCart(
        this.currentCombo.id,
        branchId,
        quantity,
        this.currentSelections,
      )
      this.setCurrentCombo(null)
    },
    resetCurrentSelections() {
      this.currentSelections = this.currentCombo
        ? getDefaultComboSelections(this.currentCombo)
        : []
    },
    clearAll() {
      this.combos = []
      this.currentCombo = null
      this.currentSelections = []
      this.error = null
    },
  },
})
